import React, { useState } from "react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowRight,
  CheckCircle2,
  Mic2,
  UserSquare2,
  CalendarRange,
  Upload,
  ImageIcon,
  Lock,
} from "lucide-react";

import "./ApplyModerator.css";

const ROLES = [
  {
    id: "moderator",
    label: "Moderator",
    icon: Mic2,
    blurb: "Anchor a single table. Steer the conversation, pull quieter voices forward.",
  },
  {
    id: "facilitator",
    label: "Facilitator",
    icon: UserSquare2,
    blurb: "Support the moderator. Pace the room, manage the seat-swaps, take notes.",
  },
  {
    id: "event-host",
    label: "Event Host",
    icon: CalendarRange,
    blurb: "Curate a full event. Pick the topics, the moderators, the guest list.",
  },
];

const EMPTY_FORM = {
  name: "",
  email: "",
  expertise: "",
  link: "",
  bio: "",
  avatar: "",
  phone: "",
  personal_email: "",
  linkedin: "",
  timezone: "",
  languages: "",
  agree_terms: false,
};

export default function ApplyModerator() {
  const [role, setRole] = useState("moderator");
  const [form, setForm] = useState(EMPTY_FORM);
  const [sent, setSent] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadHeadshot = (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Photo must be under 3 MB.");
    setUploading(true);

    const reader = new FileReader();
    reader.onload = async () => {
      const result = String(reader.result || "");
      const base64 = result.split(",")[1] || "";
      try {
        const { data } = await http.post("/upload-public-image", {
          filename: file.name,
          content_type: file.type || "image/png",
          data_base64: base64,
        });
        set("avatar", data.url);
        toast.success("Headshot uploaded.");
      } catch (er) {
        toast.error(
          formatApiErrorDetail(er.response?.data?.detail) || "Upload failed."
        );
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agree_terms) {
      toast.error("Please agree to the moderator terms.");
      return;
    }
    try {
      await http.post("/moderator-apply", { ...form, role });
      setSent(true);
      toast.success("Application received. A steward will reach out.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Submission failed"
      );
    }
  };

  return (
    <div data-testid="apply-moderator-page" className="applyModPage">
      <section
        style={{
          background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
        }}
        className="applyModHero"
      >
        <div className="container-x applyModHeroInner">
          <p className="overline applyModHeroOverline">Run the room</p>
          <h1 className="font-display applyModHeroTitle">
            Become a moderator,
            <br />
            <span className="applyModHeroAccent">facilitator or event host.</span>
          </h1>
          <p className="applyModHeroBody">
            vmeetio runs on people who can hold a room. Apply once — we'll match
            you to the tables, circles and events where your voice belongs.
          </p>
        </div>
      </section>

      <section className="section applyModSection">
        <div className="container-x applyModGrid">
          <div className="applyModRoles">
            {ROLES.map((r) => {
              const Icon = r.icon;
              const selected = role === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRole(r.id)}
                  data-testid={`role-${r.id}`}
                  className={`applyModRoleCard ${selected ? "isSelected" : "isUnselected"}`}
                >
                  <Icon size={20} className="applyModRoleIcon" />
                  <p className="overline applyModRoleLabel">{r.label}</p>
                  <p className="applyModRoleBlurb">{r.blurb}</p>
                </button>
              );
            })}
          </div>

          <div className="applyModRight">
            {sent ? (
              <div
                className="glass applyModSuccess"
                data-testid="apply-moderator-success"
              >
                <CheckCircle2 className="applyModSuccessIcon" size={32} />
                <h3 className="font-display applyModSuccessTitle">
                  Application received
                </h3>
                <p className="applyModSuccessBody">
                  A circle steward will reach out within a week.
                </p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="glass applyModForm"
                data-testid="apply-moderator-form"
              >
                {/* Headshot upload — public */}
                <div className="applyModBlock">
                  <label className="overline applyModLabelBlock">
                    Headshot (public)
                  </label>
                  <div className="applyModHeadshotRow">
                    {form.avatar ? (
                      <img
                        src={form.avatar}
                        alt="headshot"
                        className="applyModHeadshotImg"
                      />
                    ) : (
                      <div className="applyModHeadshotPlaceholder">
                        <ImageIcon size={20} />
                      </div>
                    )}

                    <label className="applyModUploadBtn">
                      <Upload size={14} />
                      {uploading
                        ? "Uploading…"
                        : form.avatar
                          ? "Replace photo"
                          : "Upload photo"}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => uploadHeadshot(e.target.files?.[0])}
                        className="applyModHiddenInput"
                        data-testid="moderator-avatar-upload"
                      />
                    </label>
                  </div>
                </div>

                <div className="applyModTwoCol">
                  <div>
                    <label className="overline applyModLabelBlock">Full name</label>
                    <input
                      required
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                      data-testid="moderator-name"
                      className="applyModInput"
                    />
                  </div>
                  <div>
                    <label className="overline applyModLabelBlock">
                      Email (public profile)
                    </label>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      data-testid="moderator-email"
                      className="applyModInput"
                    />
                  </div>
                </div>

                <div>
                  <label className="overline applyModLabelBlock">
                    Areas of expertise
                  </label>
                  <input
                    required
                    value={form.expertise}
                    onChange={(e) => set("expertise", e.target.value)}
                    data-testid="moderator-expertise"
                    placeholder="e.g. early-stage venture, climate financing"
                    className="applyModInput"
                  />
                </div>

                <div>
                  <label className="overline applyModLabelBlock">
                    LinkedIn / writing / talk (public)
                  </label>
                  <input
                    value={form.link}
                    onChange={(e) => set("link", e.target.value)}
                    data-testid="moderator-link"
                    placeholder="https://"
                    className="applyModInput"
                  />
                </div>

                <div className="applyModTwoCol">
                  <div>
                    <label className="overline applyModLabelBlock">Timezone</label>
                    <input
                      value={form.timezone}
                      onChange={(e) => set("timezone", e.target.value)}
                      data-testid="moderator-timezone"
                      placeholder="Europe/London"
                      className="applyModInput"
                    />
                  </div>
                  <div>
                    <label className="overline applyModLabelBlock">Languages</label>
                    <input
                      value={form.languages}
                      onChange={(e) => set("languages", e.target.value)}
                      data-testid="moderator-languages"
                      placeholder="English, Spanish"
                      className="applyModInput"
                    />
                  </div>
                </div>

                <div>
                  <label className="overline applyModLabelBlock">
                    A short bio — what room can you hold?
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={form.bio}
                    onChange={(e) => set("bio", e.target.value)}
                    data-testid="moderator-bio"
                    className="applyModTextarea"
                  />
                </div>

                {/* Private contact section */}
                <div className="applyModPrivateBox">
                  <p className="overline applyModPrivateOverline">
                    <Lock size={12} /> Private contact · not public
                  </p>
                  <p className="applyModPrivateHint">
                    These details are admin-only. Members reach you through the
                    platform via DMs — these fields never appear on the public
                    moderator directory.
                  </p>

                  <div className="applyModPrivateGrid">
                    <div>
                      <label className="overline applyModLabelBlock">Phone</label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => set("phone", e.target.value)}
                        data-testid="moderator-phone-private"
                        className="applyModInput"
                      />
                    </div>
                    <div>
                      <label className="overline applyModLabelBlock">Personal email</label>
                      <input
                        type="email"
                        value={form.personal_email}
                        onChange={(e) => set("personal_email", e.target.value)}
                        data-testid="moderator-personal-email"
                        className="applyModInput"
                      />
                    </div>
                    <div className="applyModSpan2">
                      <label className="overline applyModLabelBlock">
                        LinkedIn DM URL (private)
                      </label>
                      <input
                        value={form.linkedin}
                        onChange={(e) => set("linkedin", e.target.value)}
                        data-testid="moderator-linkedin-private"
                        placeholder="https://linkedin.com/in/..."
                        className="applyModInput"
                      />
                    </div>
                  </div>
                </div>

                <label
                  className="applyModTermsRow"
                  data-testid="moderator-terms-wrapper"
                >
                  <input
                    type="checkbox"
                    checked={form.agree_terms}
                    onChange={(e) => set("agree_terms", e.target.checked)}
                    data-testid="moderator-terms"
                    className="applyModCheckbox"
                  />
                  <span>
                    I confirm the details above are accurate, I'm authorised to
                    upload the headshot, and I accept the{" "}
                    <a href="#" className="applyModTermsLink">
                      moderator code of conduct
                    </a>
                    .
                  </span>
                </label>

                <p className="applyModApplyingAs">
                  Applying as:{" "}
                  <span className="applyModApplyingRole">
                    {ROLES.find((r) => r.id === role)?.label}
                  </span>
                </p>

                <button
                  data-testid="moderator-submit"
                  type="submit"
                  disabled={!form.agree_terms}
                  className="btn-gold applyModSubmit"
                >
                  Submit application <ArrowRight size={16} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}